/**
 * @fileoverview Vertical position strategy for default theme layout.
 * Places draggables on left side and targets on right side in vertical columns.
 */

import { PositionStrategy } from './PositionStrategy.js';
import { MatchingPair, ShapePositions } from "../../utilities/GameUtils.js";

/**
 * Vertical positioning strategy (default).
 * Draggables on left, targets on right, arranged vertically.
 */
export class VerticalPositionStrategy extends PositionStrategy {
    /**
     * Calculate vertical positions for shapes.
     * @param width - Canvas width
     * @param height - Canvas height
     * @param matchingPairs - Array of matching pairs
     * @param shapeSize - Size of the shapes (for bounds checking)
     * @param isSequential - Whether to use sequential positioning
     * @returns Positions with DS on left, TS on right
     */
    calculatePositions(width: number, height: number, matchingPairs: MatchingPair[], shapeSize: number, isSequential: boolean): ShapePositions {
        const ds: { x: number; y: number }[] = [];
        const ts: { x: number; y: number }[] = [];
        const numPairs = matchingPairs.length;
        
        // Calculate vertical spacing
        const availableHeight = height * 0.95; // Leave space for title
        const startY = Math.max(availableHeight * 0.15, shapeSize); // Start a bit lower to center better

        const spacing = numPairs > 5 ? availableHeight / (numPairs + 1) : availableHeight / (numPairs + 0);
        
        // Position DS on left side, TS on right side
        // Account for shape size to ensure shapes stay within canvas bounds
        // Shapes are centered at these coordinates (via offset in ShapeHelpers)
        const margin = 30; // Minimum margin from canvas edges
        const halfSize = shapeSize / 2;
        
        // DS: Ensure left edge (center - halfSize) is at least 'margin' pixels from left
        const dsX = Math.max(margin + halfSize, width * 0.12);
        
        // TS: Ensure right edge (center + halfSize) is at least 'margin' pixels from right
        const tsX = Math.min(width - margin - halfSize, width * 0.85);
        
        for (let i = 0; i < numPairs; i++) {
            const y = startY + spacing * (i + 0);
            
            ds.push({ x: dsX, y: y });
            if (!isSequential) 
                ts.push({ x: tsX, y: y });
        }

        if( isSequential ) {
            // All TS share the SAME position - centered on right side
            // Only one will be visible at a time (controlled by game logic)
            const tsCenterX = Math.min(width - margin - halfSize, width * 0.75);
            const tsCenterY = height / 2;
            
            for (let i = 0; i < numPairs; i++) {
                ts.push({ x: tsCenterX, y: tsCenterY });
            }
        }
        
        return { DS_POSITIONS: ds, TS_POSITIONS: ts };
    }

    // shape size based on available height divided by the number of pairs
    getShapeSize(height: number, _width: number, numPairs: number): number {
        let ss = Math.min(140, Math.floor(height / (numPairs * 1.5))); // Base size on available width divided by number of pairs with some spacing
        console.log(`Calculated shape size: ${ss} for height: ${height} and numPairs: ${numPairs}`);
        return Math.max(30, ss );
    }

}

